import { User } from "oidc-client-ts";
import {
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import { doGet, doPost } from "./util/apiHelper.ts";
import { getConfig } from "../services/configService";
import { getValidAccessToken } from "../auth/userManager";
import { NEEDED_ACTIONS_KEY, USER_QUERY_KEYS } from "./util/queryKeys.ts";

export type TeacherStatus = "COMPLETE" | "IMCOMPLETE";

export interface UserDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePictureUrl?: string;
  languages: string[];
  instruments: string[];
  genres: string[];
  teacherStatus?: TeacherStatus;
  introduction?: string;
  setupWizardDone: boolean;
  notificationLanguage: string;
}

export interface PostUserDto {
  firstName: string;
  lastName: string;
  email: string;
  languages?: string[];
  instruments?: string[];
  genres?: string[];
  introduction?: string;
  setupWizardDone: boolean;
  notificationLanguage?: string;
}

export interface OnboardingResponseDto {
  redirectUrl: string;
}

export const getUser = async (userId: string, user: User): Promise<UserDto> => {
  return await doGet<UserDto>(`/users/${userId}`, user);
};

export const postUser = async (
  postUserDto: PostUserDto,
  user: User,
): Promise<UserDto> => {
  return await doPost<UserDto>("/users", user, postUserDto);
};

export const postTeacherStatus = async (
  userId: string,
  user: User,
): Promise<OnboardingResponseDto> => {
  return await doPost<OnboardingResponseDto>(
    `/users/${userId}/teacher-status`,
    user,
  );
};

export const getRefreshedOnboarding = async (
  user: User,
): Promise<OnboardingResponseDto> => {
  return await doGet<OnboardingResponseDto>(
    `/users/${user.profile.sub}/onboarding/refresh`,
    user,
  );
};

const postProfilePicture = async (
  profilePicture: Blob,
  user?: User | null,
): Promise<UserDto> => {
  if (user == null) {
    throw new Error("User not present");
  }
  const config = await getConfig();
  const token = await getValidAccessToken();
  const formData = new FormData();
  formData.append("picture", profilePicture);
  const response = await fetch(
    `${config.api.baseUrl}/users/${user.profile.sub}/profile-picture`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to post profile picture");
  }

  return response.json();
};

export const useUserDataQuery = (
  userId?: string,
  options?: { enabled: boolean },
) => {
  return useAuthenticatedQuery(
    [...USER_QUERY_KEYS.userData, userId],
    async (user) => {
      if (userId) {
        return await getUser(userId, user);
      }
    },
    { ...options },
  );
};

export const useRefreshOnboardingQuery = () => {
  return useAuthenticatedQuery(["refreshed-onboarding"], async (user) => {
    return await getRefreshedOnboarding(user);
  });
};

export const usePostProfilePicture = () => {
  return useAuthenticatedMutation(
    (blob: Blob, user) => postProfilePicture(blob, user),
    { invalidateKeys: [USER_QUERY_KEYS.userData] },
  );
};

export const usePostUserQuery = () => {
  return useAuthenticatedMutation(
    (postUserDto: PostUserDto, user) => postUser(postUserDto, user),
    { invalidateKeys: [USER_QUERY_KEYS.userData] },
  );
};

export const useBecomeTeacherQuery = () => {
  return useAuthenticatedMutation(
    (_, user) => {
      return postTeacherStatus(user.profile.sub, user);
    },
    { invalidateKeys: [USER_QUERY_KEYS.userData, [NEEDED_ACTIONS_KEY]] },
  );
};
